// Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import moment from "moment-timezone";
import {
    fetchThroughput,
    fetchRecentAlerts,
    fetchTopTags,
    fetchTopMakesColours,   // uses /statsTopMakesColours
    fetchTopVisitors,
    getLogoForMake,
    prettyColourHex,        
} from "./data";
import { useNavigate } from "react-router-dom";

/* Measure the container width so the SVG scales perfectly */
function useMeasureWidth(initial = 260) {
    const ref = React.useRef(null);
    const [w, setW] = React.useState(initial);
    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const cw = entries[0].contentRect.width;
            setW(Math.max(32, Math.floor(cw)));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, w];
}

function Sparkline({ data = [], height = 44, strokeWidth = 2, tension = 1 }) {
    const [wrapRef, width] = useMeasureWidth();
    const filterId = useRef(`sparkShadow-${Math.random().toString(36).slice(2)}`).current;

    const values = data.map((d) => Number(d?.value) || 0);
    const count = values.length;

    const padX = 6, padY = 6;
    const innerW = Math.max(1, width - padX * 2);
    const innerH = Math.max(1, height - padY * 2);
    const max = Math.max(1, ...values);

    const pts = React.useMemo(() => {
        if (count === 0) return [];
        if (count === 1) {
            const x = padX + innerW / 2;
            const y = padY + innerH / 2;
            return [[x, y]];
        }
        const stepX = innerW / (count - 1);
        return values.map((v, i) => {
            const x = padX + i * stepX;
            const y = padY + (innerH - (innerH * v) / max);
            return [x, y];
        });
    }, [count, values, innerW, innerH, padX, padY, max]);

    const d = React.useMemo(() => {
        if (pts.length === 0) return "";
        if (pts.length === 1) {
            const [x, y] = pts[0];
            return `M ${x},${y} L ${x + 0.01},${y}`;
        }
        const t = tension;
        let path = `M ${pts[0][0]},${pts[0][1]}`;
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i - 1] || pts[i];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[i + 2] || pts[i + 1];
            const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * t;
            const c1y = p1[1] + ((p2[1] - p0[1]) / 6) * t;
            const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * t;
            const c2y = p2[1] - ((p3[1] - p1[1]) / 6) * t;
            path += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
        }
        return path;
    }, [pts, tension]);

    return (
        <div ref={wrapRef} className="sparkline-wrap">
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
                <defs>
                    <filter id={filterId} x="-20%" y="-50%" width="140%" height="200%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
                        <feOffset dy="0.6" in="blur" result="off" />
                        <feMerge>
                            <feMergeNode in="off" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <path
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth + 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    opacity="0.18"
                    filter={`url(#${filterId})`}
                />
                <path
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    opacity="0.9"
                />
            </svg>
        </div>
    );
}

/* Period segmented (Throughput) */
function PeriodSegmented({ value, onChange }) {
    const opt = [
        { k: "24h", label: "24 Hr" },
        { k: "7d", label: "7 Days" },
        { k: "28d", label: "28 Days" },
    ];
    return (
        <div className="segmented">
            {opt.map((o) => (
                <label key={o.k} className="segment">
                    <input type="radio" name="tp" checked={value === o.k} onChange={() => onChange(o.k)} />
                    {o.label}
                </label>
            ))}
        </div>
    );
}

function AlertTypeFlash({ color, label, keepSpace = false }) {
  // keepSpace=true if you want columns to stay aligned even when there’s no type
  if (!color && !keepSpace) return null;
  return (
    <span
      className="ra-flash"
      style={{ backgroundColor: color || "transparent" }}
      title={color ? (label || "Alert type") : undefined}
      aria-hidden="true"
    />
  );
}


/* ---- Cache helpers ---- */
const cacheKey = (period) => `dash.tp.${period}`;
function readCache(period) {
    try {
        const raw = sessionStorage.getItem(cacheKey(period));
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
function writeCache(period, payload) {
    try { sessionStorage.setItem(cacheKey(period), JSON.stringify(payload)); } catch { }
}

// Match API statsThroughput window alignment (Europe/London)
function computePeriodWindow(period) {
  const tz = "Europe/London";
  const endLocal = moment.tz(tz).seconds(0).milliseconds(0); // snap to minute
  let startLocal;
  if (period === "24h") startLocal = endLocal.clone().subtract(24, "hours");
  else if (period === "7d") startLocal = endLocal.clone().subtract(7, "days");
  else startLocal = endLocal.clone().subtract(28, "days"); // "28d"

  return { start: startLocal.toDate(), end: endLocal.toDate() };
}


/* ------------------- THROUGH­PUT CARD ------------------- */
function ThroughputCard({ period, onChange }) {
    const seed = readCache(period) || { total: 0, series: [] };
    const [series, setSeries] = useState(seed.series);
    const [total, setTotal] = useState(seed.total);
    const [loading, setLoading] = useState(false);

    const doFetch = useCallback(async (p) => {
        setLoading(true);
        try {
            const resp = await fetchThroughput(p);
            const nextTotal = resp?.total || 0;
            const nextSeries = Array.isArray(resp?.series) ? resp.series : [];
            setTotal(nextTotal);
            setSeries(nextSeries);
            writeCache(p, { total: nextTotal, series: nextSeries });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { (async () => { await doFetch(period); })(); }, [period, doFetch]);

    const debounceRef = useRef(0);
    useEffect(() => {
        const onHitsUpdated = () => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => doFetch(period), 150);
        };
        window.addEventListener("hits-updated", onHitsUpdated);
        return () => {
            clearTimeout(debounceRef.current);
            window.removeEventListener("hits-updated", onHitsUpdated);
        };
    }, [period, doFetch]);

    const nf = useMemo(() => new Intl.NumberFormat("en-GB"), []);

    return (
        <div className="dash-card card shadow-sm">
            <div className="card-header"><span style={{marginRight : 20}}><strong>Hits</strong></span>
                <PeriodSegmented value={period} onChange={onChange} />
            </div>
            <div className="dash-card-body">
                <div className="dash-card-main">
                    <div className="dash-card-number" aria-live="polite">{nf.format(total)}</div>
                </div>

                <div className="dash-card-spark"><Sparkline data={series} /></div>

                <div className={`dash-card-loading ${loading ? "is-loading" : ""}`} aria-hidden={!loading}>
                    <div className="dash-spinner" />
                </div>
            </div>
        </div>
    );
}

/* ----------------------- RECENT ALERTS ---------------------- */
function RecentAlertsCard() {
    const key = "dash.alerts.3";
    const read = () => { try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; } };
    const write = (rows) => { try { sessionStorage.setItem(key, JSON.stringify(rows)); } catch { } };

    const seed = read() || [];
    const [rows, setRows] = useState(seed);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetchRecentAlerts(3);
            const next = Array.isArray(resp?.rows) ? resp.rows : [];
            setRows(next);
            write(next);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const debounceRef = useRef(0);
    useEffect(() => {
        const refetch = () => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => load(), 150);
        };
        window.addEventListener("hits-updated", refetch);
        window.addEventListener("alertlist-updated", refetch);
        return () => {
            clearTimeout(debounceRef.current);
            window.removeEventListener("hits-updated", refetch);
            window.removeEventListener("alertlist-updated", refetch);
        };
    }, [load]);

    const goDetail = (r) => {
        const id = r.hitId || r.public_ulid;
        if (id) {
            // Pass only essentials; Detail will fetch anything missing.
            navigate("/detail/" + id, { state: { id, vrm: r.vrm, hitTime: r.hitTime } });
        } else {
            navigate("/", { state: { vrm: r.vrm, page: 1, restoreOnBack: true, source: "dashboard" } });
        }
    };

    const fmtBits = (iso) => {
        const tz = "Europe/London";
        const m = moment.utc(iso).tz(tz);
        if (!m.isValid()) return { t: "", d: "" };
        const now = moment.tz(tz).startOf("day");
        const day = m.clone().startOf("day");
        const diff = day.diff(now, "days");
        const d = diff === 0 ? "Today" : diff === -1 ? "Yest." :
            (m.year() === moment.tz(tz).year() ? m.format("ddd D MMM") : m.format("D MMM ’YY"));
        return { t: m.format("HH:mm"), d };
    };

    return (
        <div className="dash-card card shadow-sm">
            <div className="card-header"><strong>Latest Alerts</strong></div>
            <div className="card-body">
                <div className="ra-list">
                    {rows.length === 0 && <div className="ra-empty">No alert vehicles yet.</div>}
                    {rows.map((r) => {
                        const { t, d } = fmtBits(r.hitTime);
                        return (
                            <div
                                key={`${r.vrm}-${r.hitId || r.hitTime}`}
                                className="ra-row ra-row--clickable"
                                role="button"
                                tabIndex={0}
                                onClick={() => goDetail(r)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goDetail(r); } }}
                            >
                                <div className="ra-plate">
                                    <span className="registration-ui-front-small">{r.vrm}</span>
                                </div>
                                <AlertTypeFlash color={r.type_color_hex} label={r.type_label} />
                                <div className="ra-time" title={r.hitTime}>
                                    {d} {t}<br />
                                    <span className="ra-note">{r.note}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={`dash-card-loading ${loading ? "is-loading" : ""}`} aria-hidden={!loading}>
                    <div className="dash-spinner" />
                </div>
            </div>
        </div>
    );
}

/* ------------------------- Top Tags ------------------------ */
function clsForSeverity(s) {
    return s === "severe" ? "bad" : s === "warning" ? "unk" : "ok";
}
function TopTagsCard({ period }) {
    const [severity, setSeverity] = React.useState("normal");
    const sevGroupName = React.useId(); // unique per render tree
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const navigate = useNavigate();

    // fixed row layout (top-aligned when < 3 items)
    const ROW_H = 32;   // tweak if your pill is taller/shorter
    const GAP = 4;
    const containerStyle = {
        display: "grid",
        gap: GAP,
        minHeight: ROW_H * 3 + GAP * 2, // space for 3 rows (2 gaps)
        alignContent: "start",
        marginTop: 8           // top-align when fewer than 3
    };
    const rowStyle = {
        minHeight: ROW_H,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    };

    const cacheKey = (p, s) => `dash.toptags.${p}.${s}`;
    const readCache = (p, s) => {
        try { return JSON.parse(sessionStorage.getItem(cacheKey(p, s)) || "null"); } catch { return null; }
    };
    const writeCache = (p, s, data) => {
        try { sessionStorage.setItem(cacheKey(p, s), JSON.stringify(data)); } catch { }
    };

    const load = React.useCallback(async (p, s) => {
        setLoading(true);
        try {
            const resp = await fetchTopTags(p, s);
            const list = Array.isArray(resp?.rows) ? resp.rows.slice(0, 3) : []; // ensure top 3 client-side
            setRows(list);
            writeCache(p, s, list);
        } finally {
            setLoading(false);
        }
    }, []);

    // seed + fetch
    React.useEffect(() => {
        const seeded = readCache(period, severity);
        if (seeded) setRows(seeded);
        (async () => { await load(period, severity); })();
    }, [period, severity, load]);

    // refresh when new hits come in
    const debounceRef = React.useRef(0);
    React.useEffect(() => {
        const refetch = () => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => load(period, severity), 150);
        };
        window.addEventListener("hits-updated", refetch);
        return () => {
            clearTimeout(debounceRef.current);
            window.removeEventListener("hits-updated", refetch);
        };
    }, [period, severity, load]);

    // tap → apply tag filter (clears others)
    const applyTagFilter = (tagKey) => {
        navigate("/", {
            state: {
                page: 1,
                restoreOnBack: true,
                source: "toptags",
                filters: { tagKeys: [tagKey] }, // clears others by only sending this
            },
        });
    };

    return (
        <div className="dash-card card shadow-sm">
            <div className="card-header"><span style={{marginRight : 5}}><strong>Top Tags</strong></span>
            <div className="segmented">
                        {["normal", "warning", "severe"].map((k) => (
                            <label key={k} className="segment">
                                <input
                                    type="radio"
                                    name={`toptags-sev-${sevGroupName}`}   // ← unique name
                                    checked={severity === k}
                                    onChange={() => setSeverity(k)}
                                />
                                {k[0].toUpperCase() + k.slice(1)}
                            </label>
                        ))}
                    </div>
            </div>
            <div className="dash-card-body">
                <div className="tt-list" style={containerStyle}>
                    {rows.length === 0 ? (
                        <div className="ra-empty">No tags in this window.</div>
                    ) : (
                        rows.map((r) => {
                            const cls = clsForSeverity(r.status);
                            return (
                                <div key={`${r.key}-${r.status}`} style={rowStyle}>
                                    <span
                                        className={`status-pill status-pill--tag ${cls}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => applyTagFilter(r.key)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); applyTagFilter(r.key); }
                                        }}
                                        title={r.label || r.key}
                                        aria-label={`${r.label || r.key}, ${r.count} hits`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {r.label || r.key}
                                    </span>
                                    <span className="text-muted fw-bold" aria-hidden="true">
                                        {Number(r.count || 0).toLocaleString("en-GB")}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className={`dash-card-loading ${loading ? "is-loading" : ""}`} aria-hidden={!loading}>
                    <div className="dash-spinner" />
                </div>
            </div>
        </div>
    );
}


/* --------------------- Top Makes & Colours (fixed-width, non-clickable) --------------------- */
function TopMakesColoursCard({ period }) {
    const [makes, setMakes] = useState([]);
    const [colours, setColours] = useState([]);
    const [loading, setLoading] = useState(false);

    const key = (p) => `dash.topmc.${p}`;
    const read = (p) => { try { return JSON.parse(sessionStorage.getItem(key(p)) || "null"); } catch { return null; } };
    const write = (p, data) => { try { sessionStorage.setItem(key(p), JSON.stringify(data)); } catch { } };

    const load = useCallback(async (p) => {
        setLoading(true);
        try {
            const { makes = [], colours = [] } = await fetchTopMakesColours(p, 3);
            setMakes(makes.slice(0, 3));
            setColours(colours.slice(0, 3));
            write(p, { makes, colours });
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        const seeded = read(period);
        if (seeded) {
            setMakes(Array.isArray(seeded.makes) ? seeded.makes : []);
            setColours(Array.isArray(seeded.colours) ? seeded.colours : []);
        }
        (async () => { await load(period); })();
    }, [period, load]);

    const debounceRef = useRef(0);
    useEffect(() => {
        const refetch = () => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => load(period), 150);
        };
        window.addEventListener("hits-updated", refetch);
        return () => {
            clearTimeout(debounceRef.current);
            window.removeEventListener("hits-updated", refetch);
        };
    }, [period, load]);


    // Shared small styles (match alert list dialog, but smaller)
    const logoBox = {
        width: 28, height: 28, flex: "0 0 28px",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        fontSize: 10, lineHeight: "28px",
    };
    const nameTrunc = {
        minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
    };
    const countCls = "text-muted fw-bold";
    const countStyle = {
        width: 32,
        textAlign: "right",
    }

    // Fixed, table-like row grids
    const makeRowStyle = {
        display: "grid",
        gridTemplateColumns: "24px minmax(0,1fr) auto", // logo | name | count
        alignItems: "center",
        gap: 8,
        minHeight: 30,
    };
    const colourRowStyle = {
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) 18px auto", // name | swatch | count
        alignItems: "center",
        gap: 8,
        minHeight: 30,
    };

    return (
        <div className="dash-card card shadow-sm">
            <div className="dash-card-body">
                <div className="dash-card-top">
                    <div className="dash-card-title" style={{ marginTop: 5 }}>Top Makes & Colours</div>
                </div>

                <div className="mc-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                    {/* Makes (left) */}
                    <div>
                        {/* <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>Makes</div> */}
                        <div className="ra-list" style={{ gap: 4 }}>
                            {makes.length === 0 && <div className="ra-empty">No data for this window.</div>}
                            {makes.map((m) => {
                                const logo = getLogoForMake(m.make);
                                return (
                                    <div key={m.make || "unknown"} style={makeRowStyle} title={m.make || "Unknown"}>
                                        <span style={logoBox} aria-hidden="true">
                                            {logo ? <img src={logo} alt="" width={28} height={28} style={{ objectFit: "contain" }} /> : (m.make || "?").slice(0, 1).toUpperCase()}
                                        </span>
                                        <span style={nameTrunc}>{m.make || "Unknown"}</span>
                                        <span className={countCls}>{Number(m.count || 0).toLocaleString("en-GB")}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Colours (right) */}
                    <div>
                        {/* <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>Colours</div> */}
                        <div className="ra-list" style={{ gap: 4 }}>
                            {colours.length === 0 && <div className="ra-empty">No data in this window.</div>}
                            {colours.map((c) => (
                                <div key={c.colour || "unknown"} style={colourRowStyle} title={c.colour || "Unknown"}>
                                    <span style={nameTrunc}>{c.colour || "Unknown"}</span>
                                    <span
                                        className="vehicle-colourcircle"
                                        aria-hidden="true"
                                        style={{
                                            width: 18, height: 18, borderRadius: "50%",
                                            border: "1px solid rgba(0,0,0,.35)",
                                            background: prettyColourHex(c.colour),
                                            justifySelf: "center"
                                        }}
                                        title={c.colour || "Unknown"}
                                    />
                                    <span className={countCls} style={countStyle}>{Number(c.count || 0).toLocaleString("en-GB")}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`dash-card-loading ${loading ? "is-loading" : ""}`} aria-hidden={!loading}>
                    <div className="dash-spinner" />
                </div>
            </div>
        </div>
    );
}

export function FrequentVisitorsPanel({ period = "24h", title = "Most Frequent Visitors" }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();

  // small sessionStorage cache like other cards
  const keyFor = (p) => `dash.topvisitors.${p}`;
  const readCache = (p) => {
    try { return JSON.parse(sessionStorage.getItem(keyFor(p)) || "null"); } catch { return null; }
  };
  const writeCache = (p, data) => {
    try { sessionStorage.setItem(keyFor(p), JSON.stringify(data)); } catch {}
  };

  const load = React.useCallback(async (p) => {
    setLoading(true);
    try {
      const next = await fetchTopVisitors(p, 3);
      setRows(Array.isArray(next) ? next : []);
      writeCache(p, Array.isArray(next) ? next : []);
    } finally {
      setLoading(false);
    }
  }, []);

  // seed from cache + fetch on mount/period change
  React.useEffect(() => {
    const seeded = readCache(period);
    if (seeded) setRows(seeded);
    (async () => { await load(period); })();
  }, [period, load]);

  // refresh when new hits arrive (same mechanism as other cards)
  const debounceRef = React.useRef(0);
  React.useEffect(() => {
    const refetch = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => load(period), 150);
    };
    window.addEventListener("hits-updated", refetch);
    return () => {
      clearTimeout(debounceRef.current);
      window.removeEventListener("hits-updated", refetch);
    };
  }, [period, load]);

  // CLICK → set filters to VRM + period window (navigate to main table)
  const showHitsFor = (r) => {
    const { start, end } = computePeriodWindow(period);
    navigate("/", {
      state: {
        vrm: r.vrm,
        page: 1,
        startDateTime: start,
        endDateTime: end,
        restoreOnBack: true,
        source: "freqvis",
      },
    });
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header"><strong>{title}</strong></div>
      <div className="card-body">
        <div className="ra-list" style={{textAlign:"center"}}>
          {(!rows || rows.length === 0) && (
            <div className="ra-empty">No visitors yet.</div>
          )}

          {rows.map((r) => (
            <div
              key={`${r.vrm}-${r.hitId || r.last_hit || r.hits}`}
              className="ra-row ra-row--clickable"
              role="button"
              tabIndex={0}
              onClick={() => showHitsFor(r)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); showHitsFor(r); } }}
            >
              <div className="ra-plate">
                <span className="registration-ui-front-small">{r.vrm}</span>
              </div>

              <div className="ra-time" title={r.last_hit || ""}>
                {`${r.hits?.toLocaleString?.("en-GB") || r.hits || 0} hit${r.hits === 1 ? "" : "s"}`}
                <br />
                <span className="ra-note">
                  {r.make ? r.make : "Unknown make"}
                  {r.colour ? ` • ${r.colour}` : ""}
                  
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className={`dash-card-loading ${loading ? "is-loading" : ""}`} aria-hidden={!loading}>
          <div className="dash-spinner" />
        </div>
      </div>
    </div>
  );
}


/* ------------------------------- Layout --------------------------------- */
export default function Dashboard() {
    const [period, setPeriod] = useState("24h"); // single source of truth for all panels
    const navigate = useNavigate();
    const goDetail = (r) => navigate(`/detail/${encodeURIComponent(r.hitId || r.vrm)}`, { state: r });

    return (
        <div className="dashboard-grid">
            <ThroughputCard period={period} onChange={setPeriod} />
            <RecentAlertsCard />
            <TopTagsCard period={period} />
            <FrequentVisitorsPanel period={period} />
            {/* <TopMakesColoursCard period={period} /> */}
        </div>
    );
}
